import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import saveSvg from 'assets/icons/save.svg';
import editSvg from 'assets/icons/edit.svg';
import arrowRightSvg from 'assets/icons/arrow_right.svg';
import closeSvg from 'assets/icons/close.svg';
import checkSvg from 'assets/icons/check.svg';
import infoSvg from 'assets/icons/info.svg';
import barchartSvg from 'assets/icons/bar_chart.svg';
import syncedSvg from 'assets/icons/synced.svg';

import { resetForm, toggleCampaignDrawerOpen } from 'store/dealManagement/reducer';
import { getModalVisibilityConditions, getActionConditions } from 'store/dealManagement/selectors';
import { DealStatus } from 'components/common/types/Deal.types';
import Button, { Color, Size } from 'lib/Button';
import Modal from 'lib/Modal';
import AdvertiserInfoConfirmationModal from 'components/common/Modals/AdvertiserInfoConfirmationModal';
import OOCFramesModal from 'components/pages/Deals/ProgrammaticDeals/OOCFramesModal';
import { Store } from 'components/common/types/Store.types';
import { BookingCode } from 'components/pages/Planner/hooks/usePlannerActions.types';
import IconButton from '../IconButton';

interface DealActionsProps {
  saveDeal: (bookingStatusCode: BookingCode, catchHandler?: () => void) => void;
  checkAllocate?: () => void;
  dealRef?: { current: Pick<HTMLElement, 'scrollIntoView'> | null } | null;
}

const DealActions = ({
  saveDeal,
  checkAllocate = () => undefined,
  dealRef = null,
}: DealActionsProps): React.ReactElement => {
  const dispatch = useDispatch();
  const [showSaveDealErrorDialog, setShowSaveDealErrorDialog] = useState(false);
  const [showCancelDealDialog, setShowCancelDealDialog] = useState(false);
  const [showTerminateDealDialog, setShowTerminateDealDialog] = useState(false);
  const [showAdvertiserInfoConfirmationModal, setShowAdvertiserInfoConfirmationModal] = useState(false);

  const { bookingStatusCode } = useSelector((state: Store) => state.dealManagement.commonDeal);
  const actionConditions = useSelector(getActionConditions);
  const { shouldShowAdvertiserInfoModal } = useSelector(getModalVisibilityConditions);

  const isLive = bookingStatusCode === DealStatus.LIVE;

  const handleCancelDeal = (): void => {
    saveDeal(DealStatus.CANCELLED);
    setShowCancelDealDialog(false);
  };

  const handleTerminateDeal = (): void => {
    saveDeal(DealStatus.TERMINATED);
    setShowTerminateDealDialog(false);
  };

  const handleSaveDeal = (): void => {
    if (shouldShowAdvertiserInfoModal) {
      setShowAdvertiserInfoConfirmationModal(true);

      return;
    }

    saveDeal(null, () => setShowSaveDealErrorDialog(true));
  };

  return (
    <>
      <div className="space-x-3 px-6 flex items-center">
        {actionConditions.editDealInfo.show ? (
          <IconButton
            dataTestId="campaign-edit-button"
            icon={editSvg}
            tooltipText="Edit campaign info"
            isDisabled={actionConditions.editDealInfo.disabled}
            onClick={() => {
              dispatch(toggleCampaignDrawerOpen(true));
              if (dealRef?.current) dealRef.current.scrollIntoView({ behavior: 'smooth' });
            }}
          />
        ) : null}
        {actionConditions.saveDealInfo.show ? (
          <IconButton
            dataTestId="campaign-save-button"
            icon={saveSvg}
            tooltipText="Save campaign info"
            isDisabled={actionConditions.saveDealInfo.disabled}
            onClick={handleSaveDeal}
          />
        ) : null}
      </div>
      <div className="px-6 space-x-3 flex items-center">
        {actionConditions.checkAllocate.show ? (
          <IconButton
            dataTestId="check-allocate-button"
            icon={barchartSvg}
            tooltipText="Check allocation"
            isDisabled={actionConditions.checkAllocate.disabled}
            onClick={checkAllocate}
          />
        ) : null}
        {!isLive && (
          <>
            {actionConditions.reservedDeal.show && (
              <Button
                dataTestId="reserved-button"
                label="Reserve"
                svg={syncedSvg}
                onClick={() => saveDeal(DealStatus.RESERVED)}
                color={Color.TERTIARY}
                isDisabled={actionConditions.reservedDeal.disabled}
                svgClassnames="!h-[13px] !w-[13px]"
              />
            )}
            {actionConditions.pendingApprovalDeal.show ? (
              <Button
                dataTestId="pending-button"
                label="Send for approval"
                svg={arrowRightSvg}
                onClick={() => saveDeal(DealStatus.PENDING_APPROVAL)}
                color={Color.TERTIARY}
                isDisabled={actionConditions.pendingApprovalDeal.disabled}
                svgClassnames="!h-[13px] !w-[13px]"
              />
            ) : null}
            {actionConditions.rejectDeal.show ? (
              <Button
                dataTestId="reject-button"
                label="Reject"
                svg={closeSvg}
                onClick={() => saveDeal(DealStatus.REJECTED)}
                color={Color.TERTIARY}
                isDisabled={actionConditions.rejectDeal.disabled}
                svgClassnames="!h-[13px] !w-[13px]"
              />
            ) : null}
            {actionConditions.confirmDeal.show ? (
              <Button
                dataTestId="confirm-button"
                label="Confirm"
                svg={checkSvg}
                onClick={() => saveDeal(DealStatus.APPROVED)}
                color={Color.PRIMARY}
                isDisabled={actionConditions.confirmDeal.disabled}
                svgClassnames="!h-[13px] !w-[13px]"
              />
            ) : null}
            {actionConditions.cancelDeal.show ? (
              <Button
                dataTestId="cancel-button"
                label="Cancel campaign"
                svg={closeSvg}
                onClick={() => setShowCancelDealDialog(true)}
                color={Color.PRIMARY}
                isDisabled={actionConditions.cancelDeal.disabled}
              />
            ) : null}
          </>
        )}
      </div>
      <AdvertiserInfoConfirmationModal
        isVisible={showAdvertiserInfoConfirmationModal}
        onUndoChanges={() => {
          dispatch(resetForm());
          setShowAdvertiserInfoConfirmationModal(false);
        }}
        onConfirm={() => {
          setShowAdvertiserInfoConfirmationModal(false);
          saveDeal(null, () => setShowSaveDealErrorDialog(true));
        }}
      />
      <Modal
        isOpen={showSaveDealErrorDialog}
        icon={infoSvg}
        title="Oops! This campaign section cannot be saved!"
        actionButtons={
          <>
            <Button
              color={Color.DANGER}
              size={Size.LARGE}
              onClick={() => {
                dispatch(resetForm());
                setShowSaveDealErrorDialog(false);
              }}
              label="Undo Changes"
            />
            <Button
              color={Color.PRIMARY}
              size={Size.LARGE}
              onClick={() => setShowSaveDealErrorDialog(false)}
              label="Ok, I will try again"
            />
          </>
        }
      >
        <p className="ml-8 body-base text-neutral-950-opacity-60">
          Some of the last changes to the campaign section affect the availability of the lines and they cannot be
          saved. How do you want to proceed?
        </p>
      </Modal>
      <Modal
        isOpen={showCancelDealDialog}
        icon={closeSvg}
        title="Cancel campaign?"
        actionButtons={
          <>
            <Button
              color={Color.PRIMARY}
              size={Size.LARGE}
              onClick={() => setShowCancelDealDialog(false)}
              label="Close"
            />
            <Button color={Color.DANGER} size={Size.LARGE} onClick={handleCancelDeal} label="OK" />
          </>
        }
      >
        <div className="ml-8 body-base text-neutral-950-opacity-60">
          <p className="mb-2">
            All lines within this campaign will be cancelled and you will be unable to undo this change. If you proceed
            to cancel, you will be unable to edit this campaign or change the status.
          </p>
          <p>Are you sure you want to cancel this campaign?</p>
        </div>
      </Modal>
      <Modal
        isOpen={showTerminateDealDialog}
        icon={closeSvg}
        title="Terminate campaign?"
        actionButtons={
          <>
            <Button
              color={Color.PRIMARY}
              size={Size.LARGE}
              onClick={() => setShowTerminateDealDialog(false)}
              label="Cancel"
            />
            <Button color={Color.DANGER} size={Size.LARGE} onClick={handleTerminateDeal} label="OK" />
          </>
        }
      >
        <div className="ml-8 body-base text-neutral-950-opacity-60">
          <p className="mb-2">
            Terminating a live campaign will stop all trading immediately, and you will be unable to undo this.
          </p>
          <p>Are you sure you want to Terminate your campaign?</p>
        </div>
      </Modal>
      <OOCFramesModal checkAllocate={checkAllocate} />
    </>
  );
};

export default DealActions;
